import { useMemo, useState } from "react";
import axios from "axios";
import msg from "../../../../assets/images/enquiry.jpg";

const Enquiry = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    remarks: "",
  });

  const inquiryUrl = useMemo(() => {
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    // Most of the app uses REACT_APP_API_URL like: https://localhost:7202/api/
    // so we append path without repeating /api
    return `${base}/Inquiry/CreateInquiry`;
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <section className="w-full bg-gradient-to-b from-white to-orange-50/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          {/* Left: copy + image */}
          <div className="rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-sm">
            <div className="p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
                Inquiry
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
                Need help with a shipment?
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Share your details and we’ll get back to you shortly with guidance on booking,
                pricing, and delivery options.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="badge badge-outline border-orange-300 text-orange-700">
                  Fast response
                </span>
                <span className="badge badge-outline border-orange-300 text-orange-700">
                  Quote support
                </span>
                <span className="badge badge-outline border-orange-300 text-orange-700">
                  Booking guidance
                </span>
              </div>
            </div>
            <div className="relative h-56 sm:h-72 lg:h-full">
              <img src={msg} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-black/0" />
            </div>
          </div>

          {/* Right: form */}
          <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Send an inquiry</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Please fill in the form below. All fields are required.
                </p>
              </div>
              <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700 font-bold">
                ?
              </div>
            </div>

            <form
              className="mt-6"
              onSubmit={async (e) => {
                onSubmit(e);
                if (submitting) return;

                setError("");
                setSubmitting(true);
                try {
                  const now = new Date().toISOString();
                  await axios.post(
                    inquiryUrl,
                    {
                      id: 0,
                      name: form.name,
                      email: form.email,
                      mobile: form.mobile,
                      remarks: form.remarks,
                      status: "New",
                      totalCount: 0,
                      createdAt: now,
                      updatedAt: now,
                    },
                    {
                      headers: {
                        accept: "*/*",
                        "Content-Type": "application/json",
                      },
                    },
                  );

                  setSubmitted(true);
                  setForm({ name: "", email: "", mobile: "", remarks: "" });
                  setTimeout(() => setSubmitted(false), 4000);
                } catch (err) {
                  const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to send inquiry. Please try again.";
                  setError(message);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Name</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    placeholder="Your name"
                    className="input input-bordered w-full"
                    required
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Email</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="input input-bordered w-full"
                    required
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Mobile</span>
                  </label>
                  <input
                    name="mobile"
                    type="tel"
                    placeholder="Mobile number"
                    className="input input-bordered w-full"
                    required
                    value={form.mobile}
                    onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                  />
                </div>
                <div className="form-control sm:col-span-2">
                  <label className="label">
                    <span className="label-text font-medium">Remarks</span>
                  </label>
                  <textarea
                    name="remarks"
                    placeholder="Write your message..."
                    className="textarea textarea-bordered w-full min-h-[120px]"
                    required
                    value={form.remarks}
                    onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                  />
                </div>
              </div>

              {error && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              {submitted && (
                <div className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  Thanks! We received your inquiry.
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn bg-orange-500 hover:bg-orange-400 text-white border-none rounded-md px-8 disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    const el = document.getElementById("contact-us");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Contact us instead
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Enquiry;